/**
 * Pure moves for dragging on the sectioned rail (spec §2.4). An item lives in a
 * container (`ROOT`, a section id, or a parent row id — see `railLayout.ts`).
 * Every function returns the input unchanged (same reference) when the move
 * does not apply, so a caller can skip a write.
 */
import {
  containerKind,
  containerOf,
  idOf,
  isParent,
  itemsIn,
  moveWithin,
  updateContainer,
  type RailLayout,
} from '@shared/navigation/railLayout';
import { insertItem, takeItem } from '@shared/navigation/railLayoutOps';

/**
 * Move row `rowId` into container `target`, before `beforeId` when that id is in
 * the target, else at the end. A parent carries its sub-items. Refused when the
 * target is a parent's sub-items and the row is itself a parent (sub-items go one
 * level deep) or is that parent.
 */
export function moveRowToContainer(
  layout: RailLayout,
  rowId: string,
  target: string,
  beforeId?: string
): RailLayout {
  const kind = containerKind(layout, target);
  if (containerOf(layout, rowId) === undefined || kind === undefined) return layout;
  const { layout: without, item } = takeItem(layout, rowId);
  if (item === undefined) return layout;
  if (kind === 'children' && (target === rowId || isParent(item))) return layout;
  const at =
    beforeId === undefined ? -1 : itemsIn(without, target).findIndex(e => idOf(e) === beforeId);
  return insertItem(without, target, item, at);
}

/** Move `activeId` to `overId`'s place when both share a container. */
export function reorderWithin(layout: RailLayout, activeId: string, overId: string): RailLayout {
  if (activeId === overId) return layout;
  const key = containerOf(layout, activeId);
  if (key === undefined || key !== containerOf(layout, overId)) return layout;
  const items = itemsIn(layout, key);
  const from = items.findIndex(item => idOf(item) === activeId);
  const to = items.findIndex(item => idOf(item) === overId);
  return updateContainer(layout, key, list => moveWithin(list, from, to));
}
